import { useContext, useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import EmployeesTable from "./EmployeesTable";
import AddEmployeeModal from "./AddEmployeeModal";
import DeleteUserModal from "./DeleteUserModal";

import { fetchEmployeesByBranchAdmin } from "../api/employeesApi";
import { fetchCustomersByBranch } from "../api/customersApi";
import { UserSessionContext } from "../../../session/UserSessionContext";
import { useNavigation } from "../../../contexts/NavigationContext";
import useAddEmployee from "../hooks/useAddEmployee";
import useEditEmployee from "../hooks/useEditEmployee";
import useDeleteUser from "../hooks/useDeleteUser";
import { canDeleteUser, deleteKindFor } from "../domain/deleteUserRules";

export default function EmployeesPage() {
  const navigate = useNavigate();
  const { userSession } = useContext(UserSessionContext) || {};
  const { selectedBranchId, selectedBranchName } = useNavigation();

  const [employees, setEmployees] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [activeTab, setActiveTab] = useState("employees");

  const accessToken = userSession?.auth?.accessToken || sessionStorage.getItem("accessToken");
  const email = userSession?.user?.email || sessionStorage.getItem("email");
  const jobTitle = userSession?.user?.jobTitle || sessionStorage.getItem("jobTitle");
  const userId = userSession?.user?.id || sessionStorage.getItem("UserID") || "";
  const currentUserEmail = userSession?.user?.email || sessionStorage.getItem("email");
  const canEdit = jobTitle === "Organization Admin" || jobTitle === "Branch Admin";

  useEffect(() => {
    if (!selectedBranchId) {
      navigate("/branchhomepage");
    }
  }, [selectedBranchId, navigate]);

  const loadData = useCallback(async () => {
    if (!selectedBranchId || !accessToken || !email) return;

    setIsLoading(true);
    setErrorMessage("");

    try {
      const [employeesList, customersList] = await Promise.all([
        fetchEmployeesByBranchAdmin({ accessToken, email, userId }),
        fetchCustomersByBranch({ accessToken, branchId: selectedBranchId, userId }),
      ]);
      setEmployees(employeesList);
      setCustomers(customersList);
    } catch (error) {
      console.error("Failed to fetch employees/customers:", error);
      setErrorMessage("Unable to load data right now.");
    } finally {
      setIsLoading(false);
    }
  }, [accessToken, email, selectedBranchId, userId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const {
    showModal,
    openModal,
    closeModal,
    formData,
    errors,
    submitError,
    isSubmitting,
    handleChange,
    handleSubmit,
  } = useAddEmployee({
    accessToken,
    userId,
    branchId: selectedBranchId,
    onSuccess: loadData,
  });

  const {
    showModal: showEditModal,
    openModal: openEditModal,
    closeModal: closeEditModal,
    formData: editFormData,
    errors: editErrors,
    submitError: editSubmitError,
    isSubmitting: isEditSubmitting,
    handleChange: handleEditChange,
    handleSubmit: handleEditSubmit,
  } = useEditEmployee({
    accessToken,
    userId,
    onSuccess: loadData,
  });

  const {
    target: deleteTarget,
    kind: deleteKind,
    isDeleting,
    openDelete,
    closeDelete,
    confirmDelete,
  } = useDeleteUser({
    accessToken,
    userId,
    onSuccess: loadData,
  });

  const rolePriority = { org_admin: 0, branch_admin: 1, employee: 2, customer: 3 };

  const sortByRole = (a, b) => {
    const pa = rolePriority[a.role] ?? rolePriority[a.type] ?? 4;
    const pb = rolePriority[b.role] ?? rolePriority[b.type] ?? 4;
    if (pa !== pb) return pa - pb;
    const nameA = `${a.first_name} ${a.last_name}`.toLowerCase();
    const nameB = `${b.first_name} ${b.last_name}`.toLowerCase();
    return nameA.localeCompare(nameB);
  };

  const sortedEmployees = useMemo(
    () => [...employees].sort(sortByRole),
    [employees],
  );

  const allPeople = useMemo(() => {
    const emps = employees.map((e) => ({ ...e, type: "employee" }));
    const custs = customers.map((c) => ({ ...c, type: "customer" }));
    return [...emps, ...custs].sort(sortByRole);
  }, [employees, customers]);

  const rawData = activeTab === "all" ? allPeople : sortedEmployees;
  const displayData = rawData.map((person) => ({
    ...person,
    _isCurrentUser: currentUserEmail && person.email?.toLowerCase() === currentUserEmail.toLowerCase(),
  }));

  const handleEdit = (person) => {
    openEditModal(person);
  };

  // A Customer row (the All tab) goes through customers/delete.
  const handleDelete = (person) => {
    openDelete(person, deleteKindFor(person));
  };

  const canDeleteRow = (person) =>
    canDeleteUser({
      callerJobTitle: jobTitle,
      targetRole: deleteKindFor(person) === "customer" ? "customer" : person.role,
      isCurrentUser: person._isCurrentUser,
    });

  if (!selectedBranchId) {
    return null;
  }

  if (isLoading) {
    return <p>Loading...</p>;
  }

  if (errorMessage) {
    return <p>{errorMessage}</p>;
  }

  return (
    <>
      <EmployeesTable
        data={displayData}
        branchName={selectedBranchName}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onAddEmployee={openModal}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        allCount={allPeople.length}
        employeeCount={employees.length}
        canEdit={canEdit}
        canDeleteRow={canDeleteRow}
        jobTitle={jobTitle}
      />
      <AddEmployeeModal
        open={showModal}
        onClose={closeModal}
        formData={formData}
        errors={errors}
        submitError={submitError}
        isSubmitting={isSubmitting}
        handleChange={handleChange}
        handleSubmit={handleSubmit}
      />
      <AddEmployeeModal
        mode="edit"
        open={showEditModal}
        onClose={closeEditModal}
        formData={editFormData}
        errors={editErrors}
        submitError={editSubmitError}
        isSubmitting={isEditSubmitting}
        handleChange={handleEditChange}
        handleSubmit={handleEditSubmit}
      />
      <DeleteUserModal
        open={!!deleteTarget}
        user={deleteTarget}
        kind={deleteKind}
        onConfirm={confirmDelete}
        onClose={closeDelete}
        loading={isDeleting}
      />
    </>
  );
}
